import { Component, OnInit } from '@angular/core';
import { UntypedFormControl as FormControl, UntypedFormGroup as FormGroup, Validators } from '@angular/forms';
import { MatLegacyDialog as MatDialog }from '@angular/material/legacy-dialog';
import { AbstractWidgetComponent, ContextChange, HttpService, ResponseMessage, ResponseStatus, ServiceResponse, WidgetStyle } from '@sdbn/appclient';
import { MasterDetailListEntry, TranslationService } from '@sdbn/common';
import { ConfirmDialogComponent, ConfirmDialogData } from 'src/app/shared/confirm-dialog/confirm.dialog.component';
import { DialogLayout } from 'src/app/shared/dialog-layout/dialog-layout';
import { environment } from 'src/environments/environment';
import { TableSelectionDialogComponent, TableSelectionDialogData } from 'src/tmc-common/components/table-selection-dialog/table-selection-dialog.component';
import { ColumnType } from '@sdbn/appclient';
import { Row } from '@sdbn/appclient';
import { SelectionType } from '@sdbn/appclient';
import { TmcTableModel } from '@sdbn/appclient';
import { AreaGate, AreaGateAssignment, SecureKey, SecureKeyDetails } from '../secure-key.model';


@Component({
  selector: 'app-secure-key-administration',
  templateUrl: './secure-key-administration.component.html',
  styleUrls: ['./secure-key-administration.component.scss']
})
export class SecureKeyAdministrationComponent extends AbstractWidgetComponent implements OnInit {

  secureKeys = new Array<SecureKey>();

  selectedSecureKey: SecureKey;
  secureKeyDetails: SecureKeyDetails = new SecureKeyDetails({});

  responseMessages: ResponseMessage[];
  isLoading = false;

  formGroup = new FormGroup({
    usage: new FormControl('', [Validators.required]),
    active: new FormControl('', [])
  });

  get cancelEnabled() {
    return !this.isLoading && this.formGroup.dirty;
  }

  get saveEnabled() {
    return !this.isLoading && this.formGroup.dirty && this.formGroup.valid;
  }

  constructor(public translationService: TranslationService,
    private dialog: MatDialog,
    private httpService: HttpService) {
    super();

    this.formGroup.valueChanges.subscribe(formValues => {
      this.secureKeyDetails.usage = formValues.usage;
      this.secureKeyDetails.active = formValues.active;
    });

  }

  ngOnInit() {
    this.load();
  }

  onContextChange(contextChange: ContextChange) {
  }

  onSetElementAndContext() {
  }

  onSelectionChanged(entry: MasterDetailListEntry) {
    this.setSelected(entry as SecureKey);
  }

  onAddEntry(parentPath: Array<MasterDetailListEntry>) {
    this.addNew();
  }

  onDeleteEntry(entry: MasterDetailListEntry) {
    this.delete(entry as SecureKey);
  }

  public setSelected(secureKey: SecureKey) {

    if (this.selectedSecureKey != null && this.selectedSecureKey.id === secureKey.id) {
      return;
    }

    if (this.formGroup.dirty) {
      const dialogData: ConfirmDialogData = {
        titleKey: 'tmc-yard.widget.secure-key-administration.discard.confirm-dialog.title',
        messageKey: 'tmc-yard.widget.secure-key-administration.discard.confirm-dialog.message',
        confirm: () => {
          this.setSelectedWithoutCheck(secureKey);
        }
      };
      this.dialog.open(ConfirmDialogComponent, {
        data: dialogData
      });
    } else {
      this.setSelectedWithoutCheck(secureKey);
    }

  }

  public delete(secureKey: SecureKey) {

    this.responseMessages = [];

    const dialogData: ConfirmDialogData = {
      titleKey: 'tmc-yard.widget.secure-key-administration.delete.confirm-dialog.title',
      messageKey: 'tmc-yard.widget.secure-key-administration.delete.confirm-dialog.message',
      confirm: () => {
        this.setLoading(true);
        this.httpService.postWithContext<ServiceResponse>(environment.secureKeysAdminEntpoint + "/delete", secureKey.id).subscribe(response => {
          if (response.status === ResponseStatus.SUCCESS) {
            this.clearAndLoad();
          } else {
            this.responseMessages = response.responseMessages;
          }
          this.setLoading(false);
        }, error => {
          console.log(error);
          this.setLoading(false);
        });
      }
    };

    this.dialog.open(ConfirmDialogComponent, {
      data: dialogData
    });
  }

  public addNew() {
    if (this.formGroup.dirty) {
      const dialogData: ConfirmDialogData = {
        titleKey: 'tmc-yard.widget.secure-key-administration.discard.confirm-dialog.title',
        messageKey: 'tmc-yard.widget.secure-key-administration.discard.confirm-dialog.message',
        confirm: () => {
          this.clear();
        }
      };
      this.dialog.open(ConfirmDialogComponent, {
        data: dialogData
      });
    } else {
      this.clear();
    }
  }

  public cancel() {
    if (this.formGroup.dirty) {
      const dialogData: ConfirmDialogData = {
        titleKey: 'tmc-yard.widget.secure-key-administration.discard.confirm-dialog.title',
        messageKey: 'tmc-yard.widget.secure-key-administration.discard.confirm-dialog.message',
        confirm: () => {
          this.discardChanges();
        }
      };
      this.dialog.open(ConfirmDialogComponent, {
        data: dialogData
      });
    } else {
      this.discardChanges();
    }
  }

  public save() {
    this.setLoading(true);
    this.responseMessages = [];
    this.httpService.postWithContext<ServiceResponse>(environment.secureKeysAdminEntpoint + "/save", this.secureKeyDetails).subscribe(response => {
      this.setLoading(false);
      if (response.status === ResponseStatus.SUCCESS) {
        this.resetFormGroup();
        this.load();
      } else {
        this.responseMessages = response.responseMessages;
      }
    }, error => {
      console.log(error);
      this.setLoading(false);
    });
  }

  public removeAreaGateAssignment(areaGateAssignment: AreaGateAssignment) {
    const dialogData: ConfirmDialogData = {
      titleKey: 'tmc-yard.widget.secure-key-administration.remove-area-gate.confirm-dialog.title',
      messageKey: 'tmc-yard.widget.secure-key-administration.remove-area-gate.confirm-dialog.message',
      confirm: () => {
        if (areaGateAssignment.id == null) {
          const index = this.secureKeyDetails.areaGateAssignments.indexOf(areaGateAssignment);
          this.secureKeyDetails.areaGateAssignments.splice(index, 1);
        } else {
          areaGateAssignment.deleted = true;
        }
        this.formGroup.markAsDirty();
      }
    };
    this.dialog.open(ConfirmDialogComponent, {
      data: dialogData
    });
  }

  public addAreaGateAssignment() {
    this.httpService.postWithContext<AreaGate[]>(environment.secureKeysAdminEntpoint + "/area-gates", {}).subscribe(response => {

      const areaGates = new Array<AreaGate>();
      for (const key of response) {
        areaGates.push(new AreaGate(key));
      }

      this.setLoading(false);

      const tableSelectionDialogData: TableSelectionDialogData = {
        title: this.translationService.translate('tmc-yard.widget.secure-key-administration.area-gate-selection.title'),
        tableModel: this.createTableModelForStationSelectionDialog(areaGates),
        confirmCallback: (tableModel: TmcTableModel) => {
          const assignments = new Array<AreaGateAssignment>();
          for (const row of tableModel.selectedRows) {
            assignments.push(new AreaGateAssignment(
              {
                id: null,
                areaGateId: +row.id,
                areaGateName: row.values.name
              })
            );
          }
          this.secureKeyDetails.areaGateAssignments = assignments;
          this.formGroup.markAsDirty();
        }

      };

      this.dialog.open(TableSelectionDialogComponent, {
        data: tableSelectionDialogData,
        panelClass: DialogLayout.panelClass
      });

    }, error => {
      console.error(error);
      this.setLoading(false);
    });
  }

  private discardChanges() {
    this.responseMessages = [];
    if (this.secureKeyDetails.id != null) {
      this.setSelectedWithoutCheck(this.selectedSecureKey);
    } else {
      this.secureKeyDetails = new SecureKeyDetails({});
      this.secureKeyDetails.active = true;
      this.resetFormGroup();
      this.selectedSecureKey = null;
    }
  }

  private setSelectedWithoutCheck(secureKey: SecureKey) {
    if (secureKey == null) {
      return;
    }
    this.setLoading(true);
    this.responseMessages = [];
    this.httpService.postWithContext<SecureKeyDetails>(environment.secureKeysAdminEntpoint + "/details", secureKey.id).subscribe(details => {
      this.selectedSecureKey = secureKey;
      this.secureKeyDetails = new SecureKeyDetails(details);
      this.resetFormGroup();
      this.setLoading(false);
    }, error => {
      this.setLoading(false);
      console.log(error);
    });
  }

  private clear() {
    this.responseMessages = [];
    this.selectedSecureKey = null;
    this.secureKeyDetails = new SecureKeyDetails({ id: -1 });
    this.resetFormGroup();
  }

  private clearAndLoad() {
    this.clear();
    this.load();
  }

  private createTableModelForStationSelectionDialog(areaGates: AreaGate[]): TmcTableModel {
    const tableModel = new TmcTableModel();
    tableModel.selectionType = SelectionType.MULTIPLE;
    tableModel.sortable = true;
    tableModel.filterable = true;
    tableModel.sortColumn = 'name';
    tableModel.sortDirection = 'asc';
    const prefix = 'tmc-common.entity.area-gate.';
    tableModel.columnDefinitions = [
      { id: 'name', name: 'name', visible: true, type: ColumnType.STRING, translationKey: prefix + 'name' }
    ];
    tableModel.setRows(this.convertAreaGatesToRows(areaGates));
    return tableModel;
  }

  private convertAreaGatesToRows(gates: AreaGate[]): Row[] {
    const rows: Row[] = [];
    for (const gate of gates) {
      const row: Row = new Row();
      row.id = gate.id + '';
      row.values = {
        id: gate.id,
        name: gate.name
      };
      rows.push(row);
    }
    return rows;
  }

  private load() {
    this.setLoading(true);
    this.responseMessages = [];
    this.httpService.postWithContext<SecureKey[]>(environment.secureKeysAdminEntpoint, {}).subscribe(response => {

      const newSecureKeys = new Array<SecureKey>();
      for (const key of response) {
        newSecureKeys.push(new SecureKey(key));
      }
      this.secureKeys = newSecureKeys;

      if (this.secureKeys.length > 0) {
        if (this.selectedSecureKey == null) {
          this.setSelectedWithoutCheck(this.secureKeys[0]);
        } else {
          this.setSelectedWithoutCheck(this.secureKeys.find(s => s.id === this.selectedSecureKey.id));
        }
      }

      this.setLoading(false);
    }, error => {
      console.error(error);
      this.setLoading(false);
    });
  }

  private setLoading(loading: boolean) {
    this.isLoading = loading;
  }

  private resetFormGroup() {
    const values: any = {};
    values.usage = this.secureKeyDetails.usage;
    values.active = this.secureKeyDetails.active;
    this.formGroup.reset(values);
    this.formGroup.markAsPristine();
  }

  getStyle(): WidgetStyle {
    return WidgetStyle.transparent;
  }
}
